/**
 * zLayers — the app's single stacking ladder.
 *
 * Every custom z-index used to be invented locally, with no reference to MUI's
 * scale. That produced silent collisions: the floating account bar sat at 1250,
 * above MUI's drawer layer (1200), so it stayed lit and tappable on top of an
 * open drawer's dimming scrim. Chrome must sit BELOW the overlay layers, not
 * above them.
 *
 * These values deliberately mirror MUI's defaults so ours interleave correctly:
 *   mobileStepper 1000 · fab 1050 · appBar 1100
 *   drawer 1200 · modal 1300 · snackbar 1400 · tooltip 1500
 *
 * Rule of thumb: if a surface dims the app behind it, it belongs at `modal` or
 * above. If it is always-on furniture, it belongs at `chrome` or below.
 */
export const Z = {
  /** Decorative layers inside a page's own stacking context (map pins, etc). */
  mapBase: 1,
  mapOverlay: 10,
  /** Sticky page headers — scroll under the app chrome, over page content. */
  pageHeader: 50,
  /** Floating action buttons. Below chrome so they never cover the nav bar. */
  fab: 1050,
  /**
   * Panels that float over the map and its own controls — the Match Deck.
   *
   * The map paints its controls with raw numbers rather than tokens, and they
   * top out at 1050 (the location and privacy fabs at 1000, the zoom indicator
   * and the wishlist popup at 1000, the user-count badge at 1050). A panel that
   * sits in that corner has to outrank all of them, so this rung is above `fab`.
   *
   * It is deliberately BELOW `chrome` and below every overlay rung. The deck
   * does not dim the app behind it — it is map furniture, and by the rule of
   * thumb above, furniture never outranks the nav bar, a drawer or a dialog.
   * At `modalContent` it did exactly that: it painted on top of the profile
   * sheet and the Trade Arena and swallowed taps meant for them. Nothing
   * between the deck and <body> creates a stacking context, so its number
   * competes directly with the portalled overlays and has to lose.
   */
  mapPanel: 1090,
  /** Persistent app furniture: bottom nav, floating account bar. */
  chrome: 1100,
  /** Slide-in panels (MUI Drawer sits here). */
  drawer: 1200,
  /** Anything that dims the app behind it: dialogs, bottom sheets. */
  modal: 1300,
  /** The sheet itself, above its own scrim. */
  modalContent: 1310,
  /** Transient messages. Above modals so errors are never trapped behind one. */
  snackbar: 1400,
  /** Full-screen celebration moments — the top of the app. */
  celebration: 1500,
};

/**
 * Persistent chrome insets, in px. Transient surfaces (snackbars) use these to
 * stay clear of the nav bar and the account bar instead of landing on them.
 */
export const CHROME_INSET = {
  /** Bottom nav height plus breathing room. */
  bottom: 96,
  /** Account-bar band (40px control + 12px inset) plus breathing room. */
  top: 72,
};

export default Z;
